import React, { useEffect, useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { Encounter, Patient, User } from '../../types';
type PrescriptionFormPreviewProps = {
  encounter: Encounter;
  patient: Patient;
  pharmacist: User | null;
  onClose: () => void;
};
export function PrescriptionFormPreview({
  encounter,
  patient,
  pharmacist,
  onClose
}: PrescriptionFormPreviewProps) {
  const printableRef = useRef<HTMLElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const prescriptionDate = getPrescriptionDate(encounter);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    setPdfError('');
    try {
      const filename = `BMED_Prescription_${sanitizeFilename(patient.bmcFileNumber)}_${sanitizeFilename(patient.lastName)}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (error) {
      console.error('Prescription PDF generation failed', error);
      setPdfError('The prescription PDF could not be generated. Please try again or use Print.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="prescription-form-title" data-id="element-9349" data-mp="2punrivs81ww4">
      <div className="test-modal-print max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-9350" data-mp="10h6z37ve4d8">
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-9351" data-mp="au6dnz44qos3">
          <div data-id="element-9352" data-mp="3e9vcikltgzhc">
            <h2 id="prescription-form-title" className="text-lg font-bold text-slate-900" data-id="element-9353" data-mp="3e9c6o29fy1jo">
              Prescription form
            </h2>
            <p className="text-sm text-slate-500" data-id="element-9354" data-mp="3najsvlyonbxe">
              Review, print, or download the patient prescription.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-id="element-9355" data-mp="12jjbtdk6mqfp">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-9356" data-mp="12hygvmtyh5hb">
              <PrinterIcon className="mr-1.5 h-4 w-4" data-id="element-9357" data-mp="yippupqavlpg" /> Print
            </Button>
            <Button size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} isLoading={isGeneratingPdf} className="bg-teal-600 hover:bg-teal-700 focus:ring-teal-500" data-id="element-9358" data-mp="160xlu7ubiltm">
              {!isGeneratingPdf && <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-9359" data-mp="112dz457wh607" />}
              {isGeneratingPdf ? 'Generating PDF…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500" aria-label="Close prescription form" data-id="element-9360" data-mp="1ot9lunxi1b0l">
              <XIcon className="h-5 w-5" data-id="element-9361" data-mp="1k0qazyq74tzh" />
            </button>
          </div>
        </header>

        {pdfError && <p role="alert" className="mx-5 mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 print:hidden" data-id="element-9362" data-mp="27prinjr7g13p">
            {pdfError}
          </p>}

        <article ref={printableRef} className="a4-document bg-white p-7 text-slate-950 sm:p-10 print:p-0" data-id="element-9363" data-mp="ousiuphkmez3">
          <header className="flex flex-col gap-5 border-b-2 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-9364" data-mp="ev1sn001mm4t">
            <div className="flex items-center gap-4" data-id="element-9365" data-mp="9wqdc9qzbjf7">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-16 w-16 shrink-0 object-contain sm:h-20 sm:w-20" data-id="element-9366" data-mp="9ybcyds5hsjx" />
              <div data-id="element-9367" data-mp="xsl2g5ubs8sa">
                <p className="text-xl font-bold tracking-wide text-pink-600 sm:text-2xl" data-id="element-9368" data-mp="xs2y0lxieo9a">
                  BMED MEDICAL CENTRE
                </p>
                <p className="mt-1 text-sm italic text-slate-600" data-id="element-9369" data-mp="1qaqd9kptr92g">
                  Caring Healing Leading
                </p>
              </div>
            </div>
            <div className="text-left sm:text-right" data-id="element-9370" data-mp="1lhdqlg4phccc">
              <h1 className="text-xl font-bold tracking-[0.12em] text-slate-900 sm:text-2xl" data-id="element-9371" data-mp="1tt60ap0je10q">
                PRESCRIPTION FORM
              </h1>
              <p className="mt-3 text-sm" data-id="element-9372" data-mp="1xvj4h8zmxqve">
                <span className="font-semibold" data-id="element-9373" data-mp="22sj8zfgu6lke">Prescription date:</span>{' '}
                {formatDate(prescriptionDate)}
              </p>
              <p className="text-sm" data-id="element-9374" data-mp="27t6wvs9rqmae">
                <span className="font-semibold" data-id="element-9375" data-mp="2cr8lespyrdt9">Encounter date:</span>{' '}
                {formatDate(encounter.startedAt)}
              </p>
              <p className="text-sm" data-id="element-9376" data-mp="2gr0tz5fcxjym">
                <span className="font-semibold" data-id="element-9377" data-mp="2ljagebh9k1w5">Encounter:</span> {encounter.id}
              </p>
            </div>
          </header>

          <section className="my-7 border-2 border-slate-800 text-sm" aria-label="Patient details" data-id="element-9378" data-mp="2qin3mxc1raz9">
            <div className="grid gap-x-5 gap-y-3 border-b border-slate-300 p-4 sm:grid-cols-[1.8fr_0.6fr_0.6fr]" data-id="element-9379" data-mp="2vjb409m61465">
              <p data-id="element-9380" data-mp="30egk2rsvzomb">
                <span className="font-semibold" data-id="element-9381" data-mp="2asfccnbrpre3">Patient name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </p>
              <p data-id="element-9382" data-mp="25ungxjbwzn55">
                <span className="font-semibold" data-id="element-9383" data-mp="25v5m55gk1bp7">Age:</span> {patient.age}
              </p>
              <p data-id="element-9384" data-mp="2kjrzj1mmftds">
                <span className="font-semibold" data-id="element-9385" data-mp="2kkblqujb13xm">Sex:</span> {patient.sex}
              </p>
            </div>
            <div className="grid gap-x-5 gap-y-3 border-b border-slate-300 p-4 sm:grid-cols-2" data-id="element-9386" data-mp="2fpntqlp3drea">
              <p data-id="element-9387" data-mp="2tkq87x808p2p">
                <span className="font-semibold" data-id="element-9388" data-mp="2ok2ideviiea2">File no:</span>{' '}
                {patient.bmcFileNumber}
              </p>
              <p data-id="element-9389" data-mp="33hcsokwf5e47">
                <span className="font-semibold" data-id="element-9390" data-mp="33frzayluc17x">Prescribing clinician:</span>{' '}
                {encounter.lastAttendingClinician || 'Not recorded'}
              </p>
            </div>
            <div className="p-4" data-id="element-9391" data-mp="1943eifsxh4fm">
              <span className="font-semibold" data-id="element-9392" data-mp="193br3s9hid0f">Known allergies:</span>{' '}
              <span className="font-semibold text-red-700" data-id="element-9393" data-mp="145a9zcvmcnrl">
                {patient.allergies || 'None recorded'}
              </span>
            </div>
          </section>

          <section className="no-break" aria-labelledby="prescription-medications-title" data-id="element-9394" data-mp="3kqx8c7c2zy3b">
            <h2 id="prescription-medications-title" className="border-2 border-b-0 border-slate-800 bg-slate-100 px-4 py-2 text-center text-sm font-bold uppercase tracking-wide" data-id="element-9395" data-mp="360pxodsbfjd6">
              Prescribed medication
            </h2>
            <div className="overflow-x-auto" data-id="element-9396" data-mp="312xw0s1vcivs">
              <table className="w-full min-w-[660px] border-collapse text-left text-sm" data-id="element-9397" data-mp="313r53zoavkh3">
                <thead data-id="element-9398" data-mp="2n47zbdiyol7y">
                  <tr className="bg-slate-50" data-id="element-9399" data-mp="2n4zuin8ncgxp">
                    <th scope="col" className="w-12 border border-slate-800 p-2 text-center" data-id="element-9400" data-mp="2s2sxz8ikewua">
                      No.
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-9401" data-mp="q922onwv40x0">
                      Drug
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-9402" data-mp="39b0llngzeoft">
                      Dose
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-9403" data-mp="30gck88ej1qwa">
                      Route
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-9404" data-mp="2vggfu81x3xg7">
                      Frequency
                    </th>
                    <th scope="col" className="border border-slate-800 p-2" data-id="element-9405" data-mp="2qgbvg6xw2ikp">
                      Days
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-9406" data-mp="2hijgx4zzreyd">
                  {encounter.prescriptions.map((prescription, index) => <tr key={prescription.id} data-id="element-9407" data-mp="2cjynbof8vtiw" data-mpi={index}>
                      <td className="border border-slate-800 p-2 text-center" data-id="element-9408" data-mp="fqdb3vlqxbs3">
                        {index + 1}
                      </td>
                      <td className="border border-slate-800 p-2 font-semibold" data-id="element-9409" data-mp="asbsfbs8feh5">
                        {prescription.drugName}
                      </td>
                      <td className="border border-slate-800 p-2" data-id="element-9410" data-mp="n9wdzmei9zrl">
                        {prescription.dose}
                      </td>
                      <td className="border border-slate-800 p-2" data-id="element-9411" data-mp="s1nx5bvc137p">
                        {prescription.route}
                      </td>
                      <td className="border border-slate-800 p-2" data-id="element-9412" data-mp="x1sbowcojnva">
                        {prescription.frequency}
                      </td>
                      <td className="border border-slate-800 p-2" data-id="element-9413" data-mp="4aiiv151iizi">
                        {prescription.days}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </section>

          <footer className="signature-block mt-16 grid gap-10 border-t border-slate-300 pt-7 sm:grid-cols-2" data-id="element-9414" data-mp="d9dni8sfrl0p">
            <div data-id="element-9415" data-mp="1oqvhigk7t6a1">
              <div className="border-b border-slate-900 pb-1 text-sm font-medium" data-id="element-9416" data-mp="1oqc9brkd9w97">
                {encounter.lastAttendingClinician || ' '}
              </div>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-9417" data-mp="1c8os2jsz9064">
                Prescribing clinician signature
              </p>
            </div>
            <div data-id="element-9418" data-mp="1m12w5j7j2ivg">
              <div className="border-b border-slate-900 pb-1 text-sm font-medium" data-id="element-9419" data-mp="1m2nr39xr83tu">
                {pharmacist?.fullName || ' '}
              </div>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-600" data-id="element-9420" data-mp="tcowz04pdgib">
                Generated by pharmacist
              </p>
            </div>
          </footer>
        </article>
      </div>
    </div>;
}
function getPrescriptionDate(encounter: Encounter) {
  const dates = encounter.prescriptions.map(prescription => prescription.prescribedAt).filter(Boolean).map(value => new Date(value)).filter(value => !Number.isNaN(value.getTime()));
  return dates.length ? dates[0].toISOString() : encounter.startedAt;
}
function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}
function sanitizeFilename(value: string) {
  return value.replace(/[^a-z0-9-_]/gi, '_') || 'patient';
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}